import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import WaitlistForm from './WaitlistForm';
import { WAITLIST_BASE_COUNT } from '@/seed/waitlist';

/**
 * WaitlistStrip — slim "early access" status bar that sits right after the
 * Hero. Companion to the full Waitlist section at the bottom of the page.
 *
 * Design: bg-paper-2 strip (light surface, deliberately quiet), single row
 * on sm+, stacks gracefully on mobile. No big headline, no countdown stats,
 * no scrolling ticker — just a status badge, a one-line live count, and a
 * compact email field. Goal is to give early-intent visitors a one-click
 * conversion path before they even scroll to Markets, without competing
 * visually with the Hero or the full Waitlist block.
 *
 * Reuses WaitlistForm with `size="compact"` so the submit logic stays in
 * one place. The compact form's success state is an inline "✓ #N in line"
 * line that drops into the same layout slot — no card chrome.
 */

export default function WaitlistStrip(): JSX.Element {
  const { t } = useTranslation();

  return (
    <section
      id="early-access"
      className="border-rule bg-paper-2 border-b py-5"
      aria-label={t('waitlist.strip.eyebrow')}
    >
      <div className="mx-auto max-w-[1240px] px-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <span className="text-orange inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase">
              <span aria-hidden="true" className="rounded-pill bg-orange h-1.5 w-1.5" />
              {t('waitlist.strip.eyebrow')}
            </span>
            <span className="text-dim font-mono text-[11px]" aria-hidden="true">
              ·
            </span>
            <span className="text-ink text-[13px]">
              {t('waitlist.strip.copy', {
                count: WAITLIST_BASE_COUNT.toLocaleString('en-US'),
              })}
            </span>
          </div>
          <WaitlistForm size="compact" />
        </div>
      </div>
    </section>
  );
}
